import { DocumentSlice } from '@interfaces/models/doc-types';
import { Action, createReducer, on } from '@ngrx/store';
import { TransactionState } from '@store/index';
import { isNJOrPAEligible } from '@store/selectors/router/multi-inheritance/multi-inheritance-router.helpers';

import {
  rehydrateFilesFromS3,
  setS3BucketFileNames,
} from '../../actions/documents/documents.actions';
import { StartOverStateCleared } from '../../actions/start-over/start-over.actions';
import * as TaxDocumentUploadActions from '../../actions/tax-document-upload/tax-document-upload.actions';

export interface IFileMetaData {
  fileName: string;
  objectURL: string;
  isL8ITWUploaded?: boolean;
  formName?: 'L-8' | 'ITW';
  inheritanceWaiverStateName?: 'New Jersey' | 'Pennsylvania';
  s3BucketFileName?: string;
}

export interface ITaxDocumentUploadState {
  filesMetaData: IFileMetaData[];
  checkboxSelections: {
    mailingForm: boolean;
    beneficiarySubmitted: boolean;
    noFormNeeded: boolean;
  };
  isL8ITWUploadPageVisited?: boolean;
}

export const initialState: ITaxDocumentUploadState = {
  filesMetaData: [],
  checkboxSelections: {
    mailingForm: false,
    beneficiarySubmitted: false,
    noFormNeeded: false,
  },
  isL8ITWUploadPageVisited: false,
};

const taxDocumentUploadReducer = createReducer(
  initialState,
  on(TaxDocumentUploadActions.updateTaxFilesMetaData, (state, props) => {
    return {
      ...state,
      filesMetaData: props.payload,
      isL8ITWUploadPageVisited: true,
    };
  }),
  on(TaxDocumentUploadActions.restoreTaxFilesMetaData, (state, props) => {
    return {
      ...state,
      filesMetaData: props.payload,
    };
  }),
  on(TaxDocumentUploadActions.clearTaxFilesMetaData, (state) => {
    return {
      ...state,
      filesMetaData: [],
    };
  }),
  on(TaxDocumentUploadActions.updateTaxCheckboxSelections, (state, props) => {
    return {
      ...state,
      checkboxSelections: {
        ...state.checkboxSelections,
        ...props.payload,
      },
      isL8ITWUploadPageVisited: true,
    };
  }),
  on(TaxDocumentUploadActions.restoreTaxCheckboxSelections, (state, props) => {
    return {
      ...state,
      checkboxSelections: {
        ...state.checkboxSelections,
        ...props.payload,
      },
    };
  }),
  on(setS3BucketFileNames, (state, props) => ({
    ...state,
    filesMetaData: state.filesMetaData.map((file) => {
      const match = props.payload.find((p) => p.objectURL === file.objectURL);
      return match ? { ...file, s3BucketFileName: match.s3BucketFileName } : file;
    }),
  })),
  on(rehydrateFilesFromS3, (state, props) => {
    if (props.slice !== DocumentSlice.Tax) {
      return state;
    }
    return {
      ...state,
      filesMetaData: state.filesMetaData.map((file) => {
        // S3 keys are lowercased on write by uploadFile, but the extension case in a stored
        // key (e.g. from a mixed-case original upload) can differ from what the middleware
        // echoes back, so compare case-insensitively. Both sides are guarded truthy first —
        // otherwise two keyless entries (undefined === undefined) would wrongly match.
        const match = props.payload.find(
          (p) =>
            !!p.s3BucketFileName &&
            !!file.s3BucketFileName &&
            p.s3BucketFileName.toLowerCase() === file.s3BucketFileName.toLowerCase(),
        );
        return match ? { ...file, objectURL: match.objectURL } : file;
      }),
    };
  }),
  on(StartOverStateCleared, (): ITaxDocumentUploadState => ({ ...initialState })),
);

export function reducer(state: ITaxDocumentUploadState | undefined, action: Action) {
  return taxDocumentUploadReducer(state, action);
}

export const getHasClientUploadedFile = (state: ITaxDocumentUploadState) =>
  state.filesMetaData.length > 0;

export const getHasClientUploadedL8OrITWFile = (
  state: ITaxDocumentUploadState,
  inheritanceWaiverState: string,
  currentTransactionState: TransactionState,
) => ({
  isL8ITWUploaded:
    isNJOrPAEligible(inheritanceWaiverState, currentTransactionState) &&
    state.filesMetaData.some((file) => file.isL8ITWUploaded),
  inheritanceWaiverState,
});
